import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Badge,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import {
  ArrowLeft,
  Envelope,
  Telephone,
  Calendar,
  CardText,
  Award,
  GraphUp,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  GeoAlt,
} from "react-bootstrap-icons";
import api from "../../services/api";

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Updated constants for new scoring system
  const MAX_TOTAL_SCORE = 91; // Updated from 175 to 92
  const HIGH_THRESHOLD = 73; // 70+ points is High
  const MEDIUM_THRESHOLD = 46; // 50-69 points is Medium

  useEffect(() => {
    if (id) {
      fetchStudentDetails(id);
    }
  }, [id]);

  const fetchStudentDetails = async (studentId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/students/${studentId}`);

      if (response.data.success) {
        setStudent(response.data.student);
      } else {
        setError("Failed to load student details");
      }
    } catch (error) {
      console.error("Error fetching student details:", error);
      setError(error.response?.data?.error || "Failed to load student details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Updated score badge class for new 92-point scale
  const getScoreBadgeClass = (score) => {
    if (!score && score !== 0) return "secondary";
    if (score >= HIGH_THRESHOLD) return "success"; // 70+ is High
    if (score >= MEDIUM_THRESHOLD) return "warning"; // 50-69 is Medium
    return "danger"; // <=49 is Low
  };

  const getProbabilityBadgeClass = (level) => {
    if (!level) return "secondary";
    if (level === "High") return "success";
    if (level === "Medium") return "warning";
    if (level === "Low") return "danger";
    return "secondary";
  };

  // Updated score percentage calculation for 92-point scale
  const calculateScorePercentage = (score) => {
    if (!score) return 0;
    return Math.min((score / MAX_TOTAL_SCORE) * 80, 100);
  };

  // Get score category message
  const getScoreCategoryMessage = (score) => {
    if (score >= HIGH_THRESHOLD) return "Excellent Performance";
    if (score >= MEDIUM_THRESHOLD) return "Good Performance";
    return "Needs Improvement";
  };

  // Get probability message
  const getProbabilityMessage = (level) => {
    if (level === "High") return "High Success Chance";
    if (level === "Medium") return "Moderate Success Chance";
    if (level === "Low") return "Low Success Chance";
    return "Not Assessed";
  };

  // Get progress bar width for probability level
  const getProbabilityProgressWidth = (level) => {
    if (level === "High") return "90%";
    if (level === "Medium") return "60%";
    if (level === "Low") return "30%";
    return "0%";
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "40px",
            borderRadius: "20px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <Spinner animation="border" variant="primary" />
          <p
            className="mt-3 text-primary fw-semibold"
            style={{ fontSize: "1.1rem" }}
          >
            Loading student details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          padding: "20px",
        }}
      >
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          <Alert
            variant="danger"
            style={{
              borderRadius: "15px",
              border: "none",
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              background: "rgba(255,255,255,0.95)",
            }}
          >
            <Alert.Heading style={{ color: "#dc3545" }}>
              <XCircle size={24} className="me-2" />
              Error Loading Student
            </Alert.Heading>
            <p className="fs-5">{error}</p>
            <div className="d-flex gap-3 mt-4">
              <Button
                variant="outline-danger"
                onClick={() => navigate("/admin/students")}
                className="px-4 py-2"
                style={{ borderRadius: "10px" }}
              >
                <ArrowLeft className="me-2" />
                Back to Students
              </Button>
              <Button
                variant="danger"
                onClick={() => fetchStudentDetails(id)}
                className="px-4 py-2"
                style={{ borderRadius: "10px" }}
              >
                Try Again
              </Button>
            </div>
          </Alert>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div
        style={{
          minHeight: "100vh",
          padding: "20px",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Alert
            variant="warning"
            style={{
              borderRadius: "15px",
              border: "none",
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              background: "rgba(255,255,255,0.95)",
            }}
          >
            <Alert.Heading style={{ color: "#ffc107" }}>
              <FileText size={24} className="me-2" />
              Student Not Found
            </Alert.Heading>
            <p className="fs-5">
              The student you're looking for doesn't exist or has been removed.
            </p>
            <Button
              variant="outline-warning"
              onClick={() => navigate("/admin/students")}
              className="px-4 py-2 mt-3"
              style={{ borderRadius: "10px" }}
            >
              <ArrowLeft className="me-2" />
              Back to Students
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  const pageStyle = {
    minHeight: "100vh",
    padding: "20px 10px",
    width: "100%",
    maxWidth: "100vw",
    overflowX: "hidden",
  };

  const cardStyle = {
    borderRadius: "15px",
    border: "none",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    background: "rgba(255,255,255,0.95)",
  };

  const infoItemStyle = {
    borderBottom: "1px solid #e9ecef",
    padding: "15px 0",
  };

  const lastInfoItemStyle = {
    padding: "15px 0",
    borderBottom: "none",
  };

  const statsCardStyle = {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    borderRadius: "12px",
    border: "none",
  };
  const visaChancePercentage = calculateScorePercentage(student.totalScore);

  return (
    <div style={pageStyle}>
      {/* Main content container with full width */}
      <div
        style={{
          width: "100%",
          margin: "0 auto",
          padding: "0 20px",
        }}
      >
        {/* Header */}
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div style={{ flex: 1 }}>
              <h2 className="mb-1 fw-bold text-gradient">Student Profile</h2>
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "24px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  }}
                >
                  {student.name?.charAt(0) || "?"}
                </div>
                <div>
                  <h3 className="text-black mb-1">{student.name}</h3>
                  <Badge bg="light" text="dark" className="fs-6 px-3 py-2">
                    ID: {student.appointmentId}
                  </Badge>
                </div>
              </div>
            </div>
            <Button
              variant="light"
              className="d-flex align-items-center px-4 py-2"
              onClick={() => navigate("/admin/students")}
              style={{
                borderRadius: "10px",
                fontWeight: "600",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
              }}
            >
              <ArrowLeft className="me-2" />
              Back to Students
            </Button>
          </div>

          {/* Main Content */}
          <Row className="g-4">
            {/* Left Column - Basic Info */}
            <Col lg={4}>
              <Card style={cardStyle}>
                <Card.Body className="p-4">
                  <div style={infoItemStyle}>
                    <div className="d-flex align-items-center mb-2">
                      <Envelope className="me-3 text-primary" size={20} />
                      <label className="text-muted small">Email Address</label>
                    </div>
                    <a
                      href={`mailto:${student.email}`}
                      className="text-decoration-none fs-5 d-block"
                      style={{ color: "#4361ee" }}
                    >
                      {student.email}
                    </a>
                  </div>

                  <div style={infoItemStyle}>
                    <div className="d-flex align-items-center mb-2">
                      <Telephone className="me-3 text-primary" size={20} />
                      <label className="text-muted small">Phone Number</label>
                    </div>
                    <a
                      href={`tel:${student.phone}`}
                      className="text-decoration-none fs-5 d-block"
                      style={{ color: "#4361ee" }}
                    >
                      {student.phone}
                    </a>
                  </div>

                  <div style={infoItemStyle}>
                    <div className="d-flex align-items-center mb-2">
                      <GeoAlt className="me-3 text-primary" size={20} />
                      <label className="text-muted small">City</label>
                    </div>
                    <p className="fs-5 mb-0">{student.city || "N/A"}</p>
                  </div>

                  <div style={infoItemStyle}>
                    <div className="d-flex align-items-center mb-2">
                      <CardText className="me-3 text-primary" size={20} />
                      <label className="text-muted small">Student ID</label>
                    </div>
                    <Badge
                      bg="info"
                      className="fs-5 px-3 py-2"
                      style={{ borderRadius: "8px" }}
                    >
                      {student.appointmentId}
                    </Badge>
                  </div>

                  <div style={infoItemStyle}>
                    <div className="d-flex align-items-center mb-2">
                      <Calendar className="me-3 text-primary" size={20} />
                      <label className="text-muted small">
                        Account Created
                      </label>
                    </div>
                    <p className="fs-5 mb-0">{formatDate(student.createdAt)}</p>
                  </div>

                  <div style={lastInfoItemStyle}>
                    <div className="d-flex align-items-center mb-2">
                      <Clock className="me-3 text-primary" size={20} />
                      <label className="text-muted small">Last Updated</label>
                    </div>
                    <p className="fs-5 mb-0">
                      {formatDate(student.lastUpdated)}
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column - Assessment Details */}
            <Col lg={8}>
              {/* Assessment Summary */}
              <Card style={cardStyle} className="mb-4">
                <Card.Body className="p-4">
                  <h4 className="mb-4 fw-bold">
                    <Award className="me-2" />
                    Assessment Summary
                  </h4>

                  <Row className="g-4">
                    <Col md={15}>
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #28a74515 0%, #20c99715 100%)",
                          border: "2px solid #28a745",
                          borderRadius: "15px",
                          padding: "25px",
                          textAlign: "center",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center mb-3">
                          <Award size={32} className="text-success me-3" />
                          <div>
                            <label className="text-muted small d-block">
                              Assessment Progress
                            </label>
                            <span
                              className="fw-bold display-5"
                              style={{ color: "#28a745" }}
                            >
                              {visaChancePercentage.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                        <ProgressBar
                          now={visaChancePercentage}
                          variant="success"
                          style={{ height: "10px", borderRadius: "5px" }}
                        />

                        <div className="mt-3">
                          <Badge
                            bg={getScoreBadgeClass(student.totalScore)}
                            className="fs-6 px-4 py-2"
                            style={{ borderRadius: "20px" }}
                          >
                            {visaChancePercentage.toFixed(0)}% Visa chances
                          </Badge>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              {/* Answers Table - Now wider */}
              <Card style={cardStyle}>
                <Card.Body className="p-0">
                  <div className="p-4 border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                      <h4 className="mb-0 fw-bold">
                        <FileText className="me-2" />
                        Detailed Answers
                      </h4>
                      <Badge bg="primary" pill className="fs-6 px-3 py-2">
                        {student.answers?.length || 0} Questions
                      </Badge>
                    </div>
                  </div>

                  {student.answers && student.answers.length > 0 ? (
                    <div
                      className="table-responsive"
                      style={{ maxWidth: "100%", overflowX: "auto" }}
                    >
                      <Table
                        hover
                        className="mb-0"
                        style={{ minWidth: "800px" }}
                      >
                        <thead
                          style={{
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                          }}
                        >
                          <tr>
                            <th className="ps-4 py-3" style={{ width: "60px" }}>
                              #
                            </th>
                            <th className="py-3" style={{ width: "200px" }}>
                              Question
                            </th>
                            <th className="py-3" style={{ width: "300px" }}>
                              Answer
                            </th>
                            <th
                              className="py-3 text-center"
                              style={{ width: "120px" }}
                            >
                              Points
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {student.answers.map((answer, index) => (
                            <tr
                              key={index}
                              style={{
                                transition: "all 0.3s ease",
                                borderBottom: "1px solid #e9ecef",
                              }}
                            >
                              <td className="ps-4 align-middle">
                                <div
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    background: "#f8f9fa",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    color: "#495057",
                                  }}
                                >
                                  {index + 1}
                                </div>
                              </td>
                              <td className="align-middle">
                                <div className="fw-medium mb-1">
                                  {answer.questionId}
                                </div>
                                <small className="text-muted">
                                  Question {index + 1}
                                </small>
                              </td>
                              <td className="align-middle">
                                <div
                                  style={{
                                    background: "#f8f9fa",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    maxHeight: "100px",
                                    overflowY: "auto",
                                    fontSize: "0.9rem",
                                    minHeight: "60px",
                                  }}
                                >
                                  {answer.answer || "No answer provided"}
                                </div>
                              </td>
                              <td className="align-middle text-center">
                                <Badge
                                  bg={
                                    answer.points > 0 ? "success" : "secondary"
                                  }
                                  className="fs-5 px-3 py-2"
                                  style={{
                                    borderRadius: "8px",
                                    minWidth: "60px",
                                  }}
                                >
                                  {answer.points || 0}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <FileText size={64} className="text-muted mb-3" />
                      <h5 className="text-muted">No Answers Submitted</h5>
                      <p className="text-muted mb-0">
                        This student hasn't completed the assessment yet.
                      </p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default StudentDetail;
